import { Outlet, useNavigate } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { UsageIndicator } from '@/components/layout/UsageIndicator';
import { TopMenuBar } from '@/components/layout/TopMenuBar';
import { useAuthStore } from '@/store/authStore';
import { cognitoAuth } from '@/services/auth/cognito';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  ChevronsUpDown, Network, Users, Link2, CreditCard, Settings, LogOut, DollarSign, MessageSquare,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listConversations } from '@/services/api/firm-client-messages';
import { cn } from '@/lib/utils';

export function AdminLayout() {
  const { isSuperAdmin } = useAuthStore();

  // Super admin still uses the old sidebar (platform management)
  if (isSuperAdmin()) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <div className="flex-1" />
            <UsageIndicator />
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Regular firm users: new top menu bar (Word-style)
  return (
    <div>
      <TopMenuBar />
      {/* Right-side actions overlay - fixed to top-right */}
      <div className="fixed top-0 right-4 h-11 flex items-center gap-3 z-50">
        <ClientMessagesButton />
        <UsageIndicator />
        <UserMenu />
      </div>

      {/* Page content */}
      <main className="p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Header icon button for client messages. Sits left of the UsageIndicator
 * in the top-right strip. Total unread count across all client conversations
 * shows as a small rose badge top-right of the icon; polls every 60s.
 */
function ClientMessagesButton() {
  const navigate = useNavigate();
  const { data: conversations } = useQuery({
    queryKey: ['firm-client-conversations-header'],
    queryFn: listConversations,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
  const totalUnread = (conversations || []).reduce((s, c) => s + (c.firmUnread || 0), 0);

  return (
    <button
      onClick={() => navigate('/panel/client-messages')}
      className={cn(
        'relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
        'hover:bg-muted hover:text-foreground'
      )}
      aria-label={totalUnread > 0 ? `Client messages (${totalUnread} unread)` : 'Client messages'}
      title="Client messages"
    >
      <MessageSquare className="h-4 w-4" />
      {totalUnread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-none text-white ring-2 ring-background">
          {totalUnread > 99 ? '99+' : totalUnread}
        </span>
      )}
    </button>
  );
}

function UserMenu() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();

  const getInitials = (email: string) => email.substring(0, 2).toUpperCase();

  const handleLogout = async () => {
    // Await sign-out so Cognito tokens are fully cleared BEFORE the reload.
    // Otherwise the reloaded /login still sees a valid session, bounces to
    // /panel, and renders blank (no user object yet).
    try { await cognitoAuth.signOut(); } catch { /* ignore */ }
    queryClient.clear();
    logout();
    window.location.href = '/login';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="outline-none">
        <div className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted cursor-pointer">
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-[#5048e5]/10 text-[#5048e5] text-xs">
              {user?.email ? getInitials(user.email) : 'AD'}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:flex flex-col text-left">
            <span className="text-xs font-semibold leading-tight">{user?.email?.split('@')[0] || 'User'}</span>
            {user?.role === 'tenant_admin' && (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 w-fit">Admin</Badge>
            )}
          </div>
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/panel/organization')}>
          <Network className="h-4 w-4 mr-2" /> Organization
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/users')}>
          <Users className="h-4 w-4 mr-2" /> Users
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/integrations/xero')}>
          <Link2 className="h-4 w-4 mr-2" /> Integrations
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/billing')}>
          <CreditCard className="h-4 w-4 mr-2" /> Billing & Plan
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/panel/settings')}>
          <Settings className="h-4 w-4 mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
